/* ═══════════════════════════════════════════════════════════════
   REVIEW — Finish: write the reviewed block list into the live editor
   as ONE transaction (one undo step).

   It goes through the editor exactly like a normal edit, so a shared
   document (Yjs) receives it the same way it receives typing. The
   replacement is minimal: blocks that are unchanged (node-equal) stay
   the very same nodes; only the stretches that differ are replaced —
   a peer's caret in an untouched paragraph never jumps.
   ═══════════════════════════════════════════════════════════════ */

import type { EditorState, Transaction } from '@tiptap/pm/state';
import { Fragment, type Node as PMNode } from '@tiptap/pm/model';
import type { ContentBlock } from '../../shared/types';
import { blocksToDoc } from '../designer/schema';

interface Hunk {
  oldStart: number;
  oldEnd: number;
  newStart: number;
  newEnd: number;
}

/** LCS over node equality → the differing stretches, in document order. */
export function diffNodes(a: PMNode[], b: PMNode[]): Hunk[] {
  let pre = 0;
  while (pre < a.length && pre < b.length && a[pre].eq(b[pre])) pre++;
  let suf = 0;
  while (
    suf < a.length - pre &&
    suf < b.length - pre &&
    a[a.length - 1 - suf].eq(b[b.length - 1 - suf])
  ) {
    suf++;
  }
  const A = a.slice(pre, a.length - suf);
  const B = b.slice(pre, b.length - suf);
  if (!A.length && !B.length) return [];
  // Huge middles fall back to one replacement (still one transaction).
  if (A.length * B.length > 4_000_000) {
    return [{ oldStart: pre, oldEnd: pre + A.length, newStart: pre, newEnd: pre + B.length }];
  }
  const n = A.length;
  const m = B.length;
  const L: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      L[i][j] = A[i].eq(B[j]) ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
    }
  }
  const hunks: Hunk[] = [];
  let i = 0;
  let j = 0;
  let open: Hunk | null = null;
  const flush = (): void => {
    if (open) hunks.push(open);
    open = null;
  };
  while (i < n || j < m) {
    if (i < n && j < m && A[i].eq(B[j])) {
      flush();
      i++;
      j++;
      continue;
    }
    if (!open) open = { oldStart: pre + i, oldEnd: pre + i, newStart: pre + j, newEnd: pre + j };
    if (j < m && (i >= n || L[i][j + 1] >= L[i + 1][j])) {
      j++;
      open.newEnd = pre + j;
    } else {
      i++;
      open.oldEnd = pre + i;
    }
  }
  flush();
  return hunks;
}

/**
 * The ONE transaction that turns the editor's document into `blocks`.
 * Null when nothing differs. Hunks apply right-to-left so every earlier
 * position stays valid within the same transaction.
 */
export function replaceDocWithBlocks(
  state: EditorState,
  blocks: ContentBlock[]
): Transaction | null {
  const next = state.schema.nodeFromJSON(blocksToDoc(blocks) as Record<string, unknown>);
  const a: PMNode[] = [];
  const offsets: number[] = [];
  state.doc.forEach((node, offset) => {
    a.push(node);
    offsets.push(offset);
  });
  const b: PMNode[] = [];
  next.forEach((node) => b.push(node));
  const hunks = diffNodes(a, b);
  if (!hunks.length) return null;
  const tr = state.tr;
  const posOf = (i: number): number => (i < offsets.length ? offsets[i] : state.doc.content.size);
  for (let h = hunks.length - 1; h >= 0; h--) {
    const { oldStart, oldEnd, newStart, newEnd } = hunks[h];
    tr.replaceWith(posOf(oldStart), posOf(oldEnd), Fragment.fromArray(b.slice(newStart, newEnd)));
  }
  return tr.setMeta('sfReviewFinish', true);
}
